import logger from 'utils/logger';

import { css } from '@emotion/css';
import { patch, post } from 'api/queries';
import { formDataToDto, FormFields, ReminderActivityType, ReminderVia } from 'models/giftReminders';
import { useRouter } from 'next/router';
import * as React from 'react';

import { reminderSaved } from 'bindings/Segment.bs';
import GiftReminderForm from './GiftReminderForm';
import LargeHeading from 'components/LargeHeading';
import Modal from 'components/Modal';
import { Type } from 'components/Notification';
import { useRemindersContext } from './context';
import { ModalState } from 'pages/reminders/reminders';

const heading = css({
  paddingBottom: '1rem',
  whiteSpace: 'nowrap',
});

interface Props {
  onClose?: () => void;
  initValues?: FormFields;
  uid?: string;
}

const getApiQuery = (data: any, uid?: string): Promise<void> => {
  if (!uid) {
    return post('api/v3/reminders/', data, true);
  }
  return patch(`api/v3/reminders/${uid}/`, data, true);
};

const GiftReminderItemModal = ({
  initValues = {
    name: null,
    date: null,
    relationship: null,
    occasion: null,
    sendViaEmail: true,
    sendViaSms: true,
  },
}: Props) => {
  const { getReminders, setNotification, modalState, setModalState, reminderInstanceId } = useRemindersContext();
  const router = useRouter();
  const [isLoading, setIsLoading] = React.useState(false);
  const uid = modalState.uid || undefined;
  const handleClose = () => {
    setModalState({ state: ModalState.Close });
  };
  /**
   * Updates or Creates the reminder
   */
  const onSubmit = React.useCallback(
    (data: FormFields) => {
      setIsLoading(true);
      const payload = formDataToDto(data);

      getApiQuery(payload, uid)
        .then((response: any) => {
          const { occasion_date, recipient_name, occasion, relationship, send_via_email, send_via_sms } = payload;
          const { uid: reminderUid } = response;
          reminderSaved({
            doe: occasion_date,
            name: recipient_name,
            occasion,
            path: router.pathname,
            relationship,
            remind_via:
              send_via_email && send_via_sms ? ReminderVia.Both : send_via_email ? ReminderVia.Email : ReminderVia.Sms,
            reminder_id: reminderUid,
            reminder_instance_id: reminderInstanceId,
            store: 'consumer',
            type: !!uid ? ReminderActivityType.Save : ReminderActivityType.New,
          });
          setNotification({
            type: Type.Success,
            title: !!uid ? 'Your reminder has been updated!' : 'Your reminder has been created!',
          });
          handleClose();
          setIsLoading(false);
          getReminders();
        })
        .catch(error => {
          logger.error(
            {
              err: error,
              source: 'Reminders API',
            },
            'error: unable to create/update reminder'
          );
          setIsLoading(false);
        });
    },
    [handleClose, setNotification, uid]
  );

  const isEdit = modalState.state === ModalState.Edit;
  return (
    <>
      {modalState.state !== ModalState.Close && (
        <Modal onClose={handleClose} className="gift-reminder-modal">
          <h1 className={heading}>
            <LargeHeading>{isEdit ? 'Edit reminder' : 'Create a reminder'}</LargeHeading>
          </h1>
          <GiftReminderForm
            onSubmit={onSubmit}
            onCancel={handleClose}
            uid={uid}
            initialValues={modalState.initValues || initValues}
            isLoading={isLoading}
            saveBtnText={isEdit ? 'Save' : undefined}
            cancelBtnText={isEdit ? 'Cancel' : undefined}
            loadingBtnText={isEdit ? 'Saving...' : undefined}
            reminderInstanceId={reminderInstanceId}
          />
        </Modal>
      )}
    </>
  );
};

export default GiftReminderItemModal;
